// ChatDrawer.jsx

import React, { Suspense } from 'react';
import { Box, Drawer, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FocusLock from 'react-focus-lock';
import { EntryPoint } from '../Chatbot/EntryPoint';
import Chatbot from '../Chatbot/Chatbot';
import styles from './ChatDrawer.module.css';
import Constants from '../../utils/Constants';
import FocusOnTop from '../../utils/FocusOnTop';

const ChatDrawer = ({
                        merchant,
                        isOpen,
                        initialUserMessage,
                        initialAssistantMessage,
                        onClose,
                        brandTheme,
                        entryPoint,
                        assistantConfiguration,
                        initialCategories,
                        cartProductVariantInternalId
                    }) => {
    const closeDrawer = () => {
        if (window.innerWidth < Constants.MaxWidthForMobile) {
            FocusOnTop();
        }
        onClose();
    };

    // scrollbar width = window.outerWidth - document.documentElement.clientWidth
    const scrollbarWidth = (window.innerWidth || 0) - (document.documentElement?.clientWidth || 0);

    // your working selector
    const cartSelector = assistantConfiguration?.cartDrawerSelector;
    let cartWidth = null;
    if (entryPoint === EntryPoint.RespondingFromCartAssistant && cartSelector) {
        const el = document.querySelector(cartSelector);
        if (el && el.offsetParent !== null) {
            const rect = el.getBoundingClientRect();
            cartWidth = rect.width;
        }
    }

    const inCartMode = Boolean(cartWidth);
    const drawerWidth = inCartMode
        ? `calc(100% - ${cartWidth + scrollbarWidth}px)`
        : 'min(1200px, 100vw)';

    return (
        <FocusLock autoFocus={false}>
            <Drawer
                anchor="bottom"
                open={isOpen}
                onClose={closeDrawer}
                data-testid="chat-drawer"
                ModalProps={{
                    BackdropProps: {
                        sx: {
                            // dimmer covers full height, and shrinks width when in cart mode
                            backgroundColor: '#00000066',
                            height: '100%',
                            width: inCartMode ? drawerWidth : '100%',
                        }
                    }
                }}
                PaperProps={{
                    className: styles.drawerPaper,
                    elevation: 0,
                    sx: {
                        // drawer panel
                        width: drawerWidth,
                        maxWidth: '100%',
                        // left-hug in cart mode; center otherwise
                        margin: inCartMode ? 0 : '0 auto',
                        left:   inCartMode ? 0 : undefined,
                        boxShadow: inCartMode ? 'none' : undefined,
                    }
                }}
            >
                <Box id={styles.drawerContents}>
                    <Suspense fallback={<CircularProgress size={16} />}>
                        {merchant && (
                            <Chatbot
                                merchant={merchant}
                                initialUserMessage={initialUserMessage}
                                initialAssistantMessage={initialAssistantMessage}
                                initialCategories={initialCategories}
                                brandTheme={brandTheme}
                                onClose={closeDrawer}
                                entryPoint={entryPoint}
                                assistantConfiguration={assistantConfiguration}
                                cartProductVariantInternalId={cartProductVariantInternalId}
                            />
                        )}
                    </Suspense>
                    <IconButton
                        aria-label="Close"
                        data-testid="close-icon"
                        id={styles.drawerCloseIcon}
                        onClick={closeDrawer}
                    >
                        <CloseIcon
                            sx={{ fontSize: 30, stroke: 'white', fill: 'white', strokeWidth: 1 }}
                        />
                    </IconButton>
                </Box>
            </Drawer>
        </FocusLock>
    );
};

export default ChatDrawer;
